/**
 * Marketing API Service
 * Handles all marketing-related API calls
 */

import { getApiClient } from './apiClient';

const MARKETING_BASE = '/admin/marketing';

/**
 * Audience Members API
 */
export const audienceMemberApi = {
  /**
   * Get all audience members with pagination and search
   */
  getAll: async (page = 1, size = 10, query = '') => {
    const client = getApiClient();
    const params = { page, size };
    if (query) params.query = query;
    const response = await client.get(`${MARKETING_BASE}/audience-member/all`, { params });
    return response.data;
  },

  /**
   * Get all audience members with pagination and total count
   */
  getAllPaginated: async (page = 1, size = 10, query = '') => {
    const client = getApiClient();
    const params = { page, size };
    if (query) params.query = query;
    const response = await client.get(`${MARKETING_BASE}/audience-member/all-paginated`, { params });
    return response.data;
  },

  /**
   * Get distinct current software values for member filter dropdowns
   */
  getDistinctSoftware: async () => {
    const client = getApiClient();
    const response = await client.get(`${MARKETING_BASE}/audience-member/distinct-software`);
    return response.data;
  },

  /**
   * Add a new audience member
   * @param {Object} member - The audience member object
   * @param {Object} member.memberInfo - The member info (name, email, phone, cui)
   * @param {string} member.type - The member type (AUTO_SHOP or ITP_STATION)
   */
  add: async (member) => {
    const client = getApiClient();
    const response = await client.post(`${MARKETING_BASE}/audience-member/add`, member);
    return response.data;
  },

  /**
   * Update an audience member
   */
  update: async (member) => {
    const client = getApiClient();
    const response = await client.patch(`${MARKETING_BASE}/audience-member/update`, member);
    return response.data;
  },

  /**
   * Delete an audience member
   */
  delete: async (id) => {
    const client = getApiClient();
    await client.delete(`${MARKETING_BASE}/audience-member/delete/${id}`);
  },

  /**
   * Bulk import audience members from CSV
   * @param {File} file - The CSV file
   * @param {string} type - The member type (AUTO_SHOP or ITP_STATION)
   */
  bulkImport: async (file, type = 'AUTO_SHOP') => {
    const client = getApiClient();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    const response = await client.post(`${MARKETING_BASE}/audience-member/bulk-import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * Update subscription status for a member in an audience
   */
  updateSubscription: async (memberId, audienceId, subscribed) => {
    const client = getApiClient();
    await client.patch(`${MARKETING_BASE}/audience-member/subscription`, {
      memberId,
      audienceId,
      subscribed,
    });
  },

  /**
   * Check if a member is subscribed to an audience
   */
  isSubscribed: async (memberId, audienceId) => {
    const client = getApiClient();
    const response = await client.get(
      `${MARKETING_BASE}/audience-member/${memberId}/subscription/${audienceId}`
    );
    return response.data;
  },

  /**
   * Get financial reports for an audience member (max 5 years)
   */
  getFinancialReports: async (memberId, years = 5, refresh = false) => {
    const client = getApiClient();
    const response = await client.get(`${MARKETING_BASE}/audience-member/${memberId}/financial-reports`, {
      params: { years, refresh },
    });
    return response.data;
  },

  // ── Financial data bulk-sync jobs ────────────────────────────────────────

  /**
   * Trigger the POPULATE_QUEUE Quartz job.
   * Scans all members with a tax_identifier that haven't been queried yet and
   * adds them to the financial-data queue.
   */
  triggerFinancialDataPopulate: async () => {
    const client = getApiClient();
    const response = await client.post(`${MARKETING_BASE}/financial-data/populate-queue`);
    return response.data;
  },

  /**
   * Trigger the PROCESS_QUEUE Quartz job.
   * Fetches the next batch (~10) from the queue, calls ANAF, and writes results back.
   */
  triggerFinancialDataProcess: async () => {
    const client = getApiClient();
    const response = await client.post(`${MARKETING_BASE}/financial-data/process-queue`);
    return response.data;
  },

  /**
   * Get the current number of members waiting in the financial-data queue.
   */
  getFinancialQueueSize: async () => {
    const client = getApiClient();
    const response = await client.get(`${MARKETING_BASE}/financial-data/queue-size`);
    return response.data; // number
  },

  /**
   * Get the combined job status snapshot:
   * { running: boolean, currentBatchSize: number, queueSize: number }
   */
  getFinancialJobStatus: async () => {
    const client = getApiClient();
    const response = await client.get(`${MARKETING_BASE}/financial-data/job-status`);
    return response.data;
  },
};

/**
 * Audiences API
 */
export const audienceApi = {
  /**
   * Get all audiences with pagination and search
   */
  getAll: async (page = 1, size = 10, query = '') => {
    const client = getApiClient();
    const params = { page, size };
    if (query) params.query = query;
    const response = await client.get(`${MARKETING_BASE}/audience/all`, { params });
    return response.data;
  },

  /**
   * Add a new audience
   */
  add: async (audience) => {
    const client = getApiClient();
    const response = await client.post(`${MARKETING_BASE}/audience/add`, audience);
    return response.data;
  },

  /**
   * Add a new audience populated from filters
   */
  addWithFilters: async (audience, {
    query = null,
    type = null,
    interest = null,
    status = null,
    currentSoft = null,
    minTurnover = null,
    maxTurnover = null,
    notInAudienceId = null,
    sortBy = 'created_at',
    sortAsc = false,
  } = {}) => {
    const client = getApiClient();
    const params = { sortBy, sortAsc };
    if (query) params.query = query;
    if (type !== null) params.type = type;
    if (interest !== null) params.interest = interest;
    if (status !== null) params.status = status;
    if (currentSoft) params.currentSoft = currentSoft;
    if (minTurnover !== null && minTurnover !== '') params.minTurnover = minTurnover;
    if (maxTurnover !== null && maxTurnover !== '') params.maxTurnover = maxTurnover;
    if (notInAudienceId) params.notInAudienceId = notInAudienceId;

    const response = await client.post(`${MARKETING_BASE}/audience/add-with-filters`, audience, { params });
    return response.data;
  },

  /**
   * Update an audience
   */
  update: async (audience) => {
    const client = getApiClient();
    const response = await client.patch(`${MARKETING_BASE}/audience/update`, audience);
    return response.data;
  },

  /**
   * Delete an audience
   */
  delete: async (id) => {
    const client = getApiClient();
    await client.delete(`${MARKETING_BASE}/audience/delete/${id}`);
  },

  /**
   * Get all members (with full info) for an audience
   */
  getMembers: async (audienceId, page = 1, size = 10) => {
    const client = getApiClient();
    const response = await client.get(`${MARKETING_BASE}/audience/all-members`, {
      params: { audienceId, page, size },
    });
    return response.data;
  },

  /**
   * Get subscribed members for an audience (excludes unsubscribed)
   */
  getSubscribedMembers: async (audienceId, page = 1, size = 10) => {
    const client = getApiClient();
    const response = await client.get(`${MARKETING_BASE}/audience/${audienceId}/subscribed-members`, {
      params: { page, size },
    });
    return response.data;
  },

  /**
   * Create an audience from a CSV file.
   * If a member with the same email exists, use that member. Otherwise, create a new member.
   * @param {File} file - The CSV file
   * @param {string} name - The audience name
   * @param {string} description - The audience description
   * @param {string} channel - The notification channel (EMAIL, SMS, WHATSAPP)
   * @param {string} type - The member type for new members (AUTO_SHOP or ITP_STATION)
   */
  createFromCsv: async (file, name, description, channel, type = 'AUTO_SHOP') => {
    const client = getApiClient();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    formData.append('description', description || '');
    formData.append('channel', channel);
    formData.append('type', type);
    const response = await client.post(`${MARKETING_BASE}/audience/create-from-csv`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

/**
 * Campaigns API
 */
export const campaignApi = {
  /**
   * Get all campaigns with pagination and search
   */
  getAll: async (page = 1, size = 10, query = '') => {
    const client = getApiClient();
    const params = { page, size };
    if (query) params.query = query;
    const response = await client.get(`${MARKETING_BASE}/campaign/all`, { params });
    return response.data;
  },

  /**
   * Add a new campaign
   */
  add: async (campaign) => {
    const client = getApiClient();
    const response = await client.post(`${MARKETING_BASE}/campaign/add`, campaign);
    return response.data;
  },

  /**
   * Update a campaign
   */
  update: async (campaign) => {
    const client = getApiClient();
    const response = await client.patch(`${MARKETING_BASE}/campaign/update`, campaign);
    return response.data;
  },

  /**
   * Delete a campaign (only DRAFT or CANCELLED)
   */
  delete: async (id) => {
    const client = getApiClient();
    await client.delete(`${MARKETING_BASE}/campaign/delete/${id}`);
  },

  /**
   * Upload an attachment for campaigns
   */
  uploadAttachment: async (file, filename) => {
    const client = getApiClient();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('filename', filename);
    const response = await client.post(`${MARKETING_BASE}/add-attachment`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * Get campaign analytics summary
   */
  getAnalytics: async (campaignId) => {
    const client = getApiClient();
    const response = await client.get(`${MARKETING_BASE}/campaign/${campaignId}/analytics`);
    return response.data;
  },

  /**
   * Get WhatsApp campaign analytics summary
   */
  getWhatsappAnalytics: async (campaignId) => {
    const client = getApiClient();
    const response = await client.get(`${MARKETING_BASE}/campaign/${campaignId}/whatsapp-analytics`);
    return response.data;
  },

  /**
   * Get email logs for a campaign
   */
  getEmailLogs: async (campaignId, page = 1, size = 20, status = null, sortBy = null, sortDir = null) => {
    const client = getApiClient();
    const params = { page, size };
    if (status) params.status = status;
    if (sortBy) params.sortBy = sortBy;
    if (sortDir) params.sortDir = sortDir;
    const response = await client.get(`${MARKETING_BASE}/campaign/${campaignId}/logs`, { params });
    return response.data;
  },

  /**
   * Get WhatsApp logs for a campaign
   */
  getWhatsappLogs: async (campaignId, page = 1, size = 20, status = null) => {
    const client = getApiClient();
    const params = { page, size };
    if (status) params.status = status;
    const response = await client.get(`${MARKETING_BASE}/campaign/${campaignId}/whatsapp-logs`, { params });
    return response.data;
  },

  /**
   * Pause a running campaign
   */
  pause: async (campaignId) => {
    const client = getApiClient();
    await client.post(`${MARKETING_BASE}/campaign/${campaignId}/pause`);
  },

  /**
   * Resume a paused campaign
   */
  resume: async (campaignId) => {
    const client = getApiClient();
    await client.post(`${MARKETING_BASE}/campaign/${campaignId}/resume`);
  },

  /**
   * Start a campaign immediately (Send Now)
   */
  startNow: async (campaignId) => {
    const client = getApiClient();
    await client.post(`${MARKETING_BASE}/campaign/${campaignId}/start`);
  },

  /**
   * Send a test email for a campaign
   * @param campaignId - The campaign ID
   * @param emails - Array of email addresses to send test emails to
   */
  sendTestEmail: async (campaignId, emails) => {
    const client = getApiClient();
    const response = await client.post(`${MARKETING_BASE}/campaign/${campaignId}/test-email`, { emails });
    return response.data;
  },
};

/**
 * Reports API
 */
export const reportsApi = {
  /**
   * Get member report sorted by email opens (legacy)
   * @param sortAsc - if true, sort ascending; if false (default), sort descending
   */
  getMemberReport: async (page = 1, size = 20, sortAsc = false) => {
    const client = getApiClient();
    const response = await client.get(`${MARKETING_BASE}/reports/members`, {
      params: { page, size, sortAsc },
    });
    return response.data;
  },

  /**
   * Get enhanced member report with search, filters, and sorting
   * @param page - Page number
   * @param size - Page size
   * @param query - Search term for name/email/phone/cui
   * @param type - Filter by type (0=AUTO_SHOP, 1=ITP_STATION)
   * @param interest - Filter by interest (0-3)
   * @param status - Filter by CRM status (0-4)
   * @param minTurnover - Minimum last known turnover
   * @param maxTurnover - Maximum last known turnover
   * @param sortBy - Sort column: "name", "type", "interest", "status", "followUpDate", "opens", "turnover", "created_at"
   * @param sortAsc - Sort direction
   */
  getMemberReportEnhanced: async (page = 1, size = 20, {
    query = null,
    type = null,
    interest = null,
    status = null,
    currentSoft = null,
    minTurnover = null,
    maxTurnover = null,
    sortBy = 'created_at',
    sortAsc = false,
  } = {}) => {
    const client = getApiClient();
    const params = { page, size, sortBy, sortAsc };
    if (query) params.query = query;
    if (type !== null) params.type = type;
    if (interest !== null) params.interest = interest;
    if (status !== null) params.status = status;
    if (currentSoft) params.currentSoft = currentSoft;
    if (minTurnover !== null && minTurnover !== '') params.minTurnover = minTurnover;
    if (maxTurnover !== null && maxTurnover !== '') params.maxTurnover = maxTurnover;
    const response = await client.get(`${MARKETING_BASE}/reports/members-enhanced`, { params });
    return response.data;
  },

  /**
   * Get audience report sorted by email opens
   * @param sortAsc - if true, sort ascending; if false (default), sort descending
   */
  getAudienceReport: async (page = 1, size = 20, sortAsc = false) => {
    const client = getApiClient();
    const response = await client.get(`${MARKETING_BASE}/reports/audiences`, {
      params: { page, size, sortAsc },
    });
    return response.data;
  },

  /**
   * Get campaign report sorted by email opens
   * @param sortAsc - if true, sort ascending; if false (default), sort descending
   */
  getCampaignReport: async (page = 1, size = 20, sortAsc = false) => {
    const client = getApiClient();
    const response = await client.get(`${MARKETING_BASE}/reports/campaigns`, {
      params: { page, size, sortAsc },
    });
    return response.data;
  },
};

/**
 * CRM Communication API
 */
export const communicationApi = {
  /**
   * Get communications for a member with pagination
   */
  getByMemberId: async (memberId, page = 1, size = 20) => {
    const client = getApiClient();
    const response = await client.get(`${MARKETING_BASE}/communication/member/${memberId}`, {
      params: { page, size },
    });
    return response.data;
  },

  /**
   * Get a single communication by ID
   */
  getById: async (communicationId) => {
    const client = getApiClient();
    const response = await client.get(`${MARKETING_BASE}/communication/${communicationId}`);
    return response.data;
  },

  /**
   * Add a new communication
   * @param {Object} communication - The communication object
   * @param {string} communication.memberId - The member ID
   * @param {string} communication.communicationType - NOTE, CALL, EMAIL, WHATSAPP, SMS, MEETING
   * @param {string} communication.direction - OUTBOUND or INBOUND
   * @param {string} communication.subject - Optional subject
   * @param {string} communication.content - The communication content
   * @param {string} communication.followUpDate - Optional follow-up date
   * @param {string} communication.communicationDate - The date of the communication
   */
  add: async (communication) => {
    const client = getApiClient();
    const response = await client.post(`${MARKETING_BASE}/communication/add`, communication);
    return response.data;
  },

  /**
   * Update an existing communication
   * @param {string} communicationId - The communication ID
   * @param {Object} updates - The fields to update
   */
  update: async (communicationId, updates) => {
    const client = getApiClient();
    const response = await client.patch(`${MARKETING_BASE}/communication/${communicationId}`, updates);
    return response.data;
  },

  /**
   * Mark a follow-up as completed
   */
  markFollowUpCompleted: async (communicationId) => {
    const client = getApiClient();
    const response = await client.patch(
      `${MARKETING_BASE}/communication/${communicationId}/mark-follow-up-completed`,
      {}
    );
    return response.data;
  },

  /**
   * Delete a communication
   */
  delete: async (communicationId) => {
    const client = getApiClient();
    await client.delete(`${MARKETING_BASE}/communication/delete/${communicationId}`);
  },
};

const marketingApi = {
  audienceMemberApi,
  audienceApi,
  campaignApi,
  reportsApi,
  communicationApi,
};

export default marketingApi;

